import axios from 'axios';
import {
  registerServiceWorkerAndGetToken,
  onForegroundMessage,
  removeFcmToken,
} from './firebase';
import { toast } from 'react-toastify';
import React, { useContext, useEffect } from 'react';
import { AuthContext } from './contexts/user-context/user-context';
import { useNotifications } from './contexts/user-context/notification-context';
import { API_ENDPOINT } from './configurations/global.config';
import { NotificationItem, NotificationTemplate } from './interfaces/common/notifications';
import { useSnackBar } from './contexts/snackbar';
import { Box, Typography } from '@mui/material';
import { templateIconMap } from './components/common/notifications-bell';
import { clearStoredNotifications, getStoredNotifications } from './utils/indexed-notifications-db';

interface NotificationToastProps {
    title: string;
    body: string;
    templateName: NotificationTemplate;
    closeToast?: () => void; // react-toastify will inject this
  }
  
  const NotificationToast: React.FC<NotificationToastProps> = ({ title, body, templateName }) => {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ maxWidth: 300 }}>
        {templateIconMap[templateName]}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {title}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
            {body}
          </Typography>
        </Box>
      </Box>
    );
  };


const PushNotificationsInitializer: React.FC = () => {
  const { user, config } = useContext(AuthContext);
  const { addNotification, notifications,  } = useNotifications();
  const { showMessage } = useSnackBar(); // custom snackbar handler

  const notificationPlayer = new Howl({
    src: ['/media/notification.mp3'],
    volume: 0.075,
  });

  const showNewNotificationToast = (notification: Omit<NotificationItem, 'timestamp' | 'isRead'>) => {
    const latest = notification
    showMessage(
        <NotificationToast
            title={notification.title}
            body={notification.body}
            templateName={notification.templateName}
        />,
        'info',
        { override: true, id: "notification", toastOptions: { icon: false } }
      );
  }

  useEffect(() => {
    if (!user) return;

    async function setupPush() {
      try {
        // 1️⃣ Register device with backend
        const result = await registerServiceWorkerAndGetToken();
        if (result?.token) {
          await axios.post(
            `${API_ENDPOINT}/v1/notifications/register-device/`,
            {
              device_token: result.token,
              device_type: 'web',
              language_preference: 'en',
            },
            config
          );
        }

        // 2️⃣ Handle incoming foreground messages
        onForegroundMessage((payload) => {
          console.log('Foreground message received:', payload);

          const title = payload.notification?.title || 'Notification';
          const body = payload.notification?.body || '';
          const data = payload.data

          if (!data) {
            console.log("No data found in received notification. Aborting..")
            return
          }

          const notifType = (['info', 'success', 'warning', 'error'].includes(data.type) ? data.type : 'info') as ('info' | 'success' | 'warning' | 'error')

     

          const logId = parseInt(data.notification_log_id, 10);

            if (Number.isNaN(logId)) {
                console.error('Failed to parse notification_log_id:', data.notification_log_id);
                return
            } else {
            // logId is a valid integer
                console.log('Parsed log ID:', logId);
            }

          const notificationItem : Omit<NotificationItem, 'timestamp' | 'isRead'> = {
              id: logId.toString(),
              title,
              body,
              data,
              actionUrl: data.code_url,
              notificationId: data.notification_id,
              notificationLogId: logId,
              templateName: data.template_name as NotificationTemplate,
              type: notifType,
          }

          console.log("Constructed notification item: ", notificationItem)


          // 3️⃣ Add notification to context
          addNotification(notificationItem);
          notificationPlayer.play();
          showNewNotificationToast(notificationItem);
        });

        // 3️⃣ Load any background notifications from IndexedDB
      const backgroundNotifications = await getStoredNotifications();
        backgroundNotifications.forEach((notif) => {
          addNotification(notif);
          showNewNotificationToast(notif);
        });

        // optional: clear stored background notifications so they are not duplicated next time
        clearStoredNotifications();
        
      } catch (err) {
        console.error('Error initializing push notifications:', err);
      }
    }

    setupPush();

    return () => {
      // optional cleanup on logout
    //  removeFcmToken();
    };
  }, [user, config, addNotification]);

  return null;
};

export default PushNotificationsInitializer;
